/**
 * PATH: packages/email-hub/src/feature/components/email-context/JobSiblingEmailList.tsx
 * FILE_ID: EMAILHUB-EMAILCONTEXT-SIBLINGLIST-001
 * PURPOSE: Render the other emails linked to this job as jump-to rows.
 * WHY: Lifted out of EmailJobContextRail's third section so the tablet-width
 *      header chip opens the same rows — sender, date, subject, attachment
 *      count and the one-click swap — rather than a reduced summary of them.
 * FLOW:
 * ┌────────────┐   ┌───────────────┐   ┌──────────────────────────┐
 * │ siblings[]  │ → │ row per email │ → │ onJumpTo(id) on press     │
 * └────────────┘   └───────────────┘   └──────────────────────────┘
 * RELATED:
 * - ./useJobSiblingEmails.ts: produces siblings + isPending.
 */
import { Paperclip } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import { formatShortDate, getInitialsFromNameOrEmail } from './emailContextFormat'
import type { SiblingEmail } from './useJobSiblingEmails'

export interface JobSiblingEmailListProps {
  siblings: readonly SiblingEmail[]
  isPending: boolean
  /** Swap the reader to that email without a route change. */
  onJumpTo: (siblingId: string) => void
}

export function JobSiblingEmailList({
  siblings,
  isPending,
  onJumpTo,
}: JobSiblingEmailListProps) {
  if (isPending) {
    return (
      <p className="text-[11px] italic text-muted-foreground">
        Loading siblings…
      </p>
    )
  }
  if (siblings.length === 0) {
    return (
      <p className="text-[11px] italic text-muted-foreground">
        No other emails linked to this job yet.
      </p>
    )
  }
  return (
    <ul className="space-y-1.5" data-testid="job-sibling-email-list">
      {siblings.map((sibling) => {
        const initials = getInitialsFromNameOrEmail(
          sibling.from_name,
          sibling.from_address,
        )
        return (
          <li key={sibling.id}>
            <button
              type="button"
              onClick={() => onJumpTo(sibling.id)}
              className={cn(
                'w-full rounded-md px-2 py-1.5 text-left transition-colors',
                'hover:bg-accent/60',
                !sibling.is_read && 'bg-primary/[0.04]',
              )}
              title={`Open: ${sibling.subject || '(no subject)'}`}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'truncate text-[11px]',
                        sibling.is_read
                          ? 'text-foreground/70'
                          : 'font-semibold text-foreground',
                      )}
                      title={sibling.from_name || sibling.from_address}
                    >
                      {sibling.from_name || sibling.from_address.split('@')[0]}
                    </span>
                    <span className="shrink-0 text-[10px] text-muted-foreground">
                      {formatShortDate(sibling.received_at)}
                    </span>
                  </div>
                  <p
                    className={cn(
                      'truncate text-[11px]',
                      sibling.is_read
                        ? 'text-foreground/60'
                        : 'font-medium text-foreground',
                    )}
                    title={sibling.subject || '(no subject)'}
                  >
                    {sibling.subject || '(no subject)'}
                  </p>
                  {sibling.has_attachments && (
                    <span className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                      <Paperclip className="h-2.5 w-2.5" />{' '}
                      {sibling.attachment_count || ''}
                    </span>
                  )}
                </div>
              </div>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
